'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  ConversionInput,
  EventingAdapter,
  PageViewInput,
} from '../adapters/types';
import { subscribeDelegatedInteractions } from '../capture/delegated-interactions';
import { startViewTracker } from '../capture/view-tracker';
import type {
  InteractionRegistry,
  RegistryPolicy,
  ViewRegistry,
} from '../registry/types';
import { resolveEventRequestContext } from '../request-context/resolveEventRequestContext';
import type { EventingContext } from '../types/context';

const DEFAULT_REGISTRY_POLICY: RegistryPolicy = {
  unknownComponent: 'ignore',
};

const EMPTY_CONTEXT: EventingContext = {};

export interface EventingApi {
  readonly trackPageView: (pageUrl: PageViewInput['pageUrl']) => void;
  readonly trackConversion: (conversion: ConversionInput) => void;
}

const EventingApiContext = createContext<EventingApi | null>(null);

export interface EventingProviderProps {
  readonly children: ReactNode;
  readonly adapter: EventingAdapter;
  readonly interactionRegistry: InteractionRegistry;
  readonly viewRegistry: ViewRegistry;
  readonly context?: EventingContext;
  readonly registryPolicy?: RegistryPolicy;
  readonly root?: Document | Element;
  readonly viewRoot?: Document | Element;
  readonly onUnknownComponentId?: (componentId: string) => void;
}

function resolveObservationRoot(
  root: Document | Element | undefined,
): Document | Element | null {
  if (root) {
    return root;
  }
  if (typeof document !== 'undefined') {
    return document;
  }
  return null;
}

export function EventingProvider({
  children,
  adapter,
  interactionRegistry,
  viewRegistry,
  context,
  registryPolicy = DEFAULT_REGISTRY_POLICY,
  root,
  viewRoot,
  onUnknownComponentId,
}: EventingProviderProps) {
  const eventingContext = useMemo(
    () => resolveEventRequestContext(context ?? EMPTY_CONTEXT),
    [context],
  );
  const observationRoot = useMemo(() => resolveObservationRoot(root), [root]);
  const viewObservationRoot = useMemo(
    () => resolveObservationRoot(viewRoot ?? root),
    [viewRoot, root],
  );
  const trackPageView = useCallback(
    (pageUrl: string): void => {
      adapter.handlePageView?.(
        {
          pageUrl,
          timestamp: Date.now(),
        },
        eventingContext,
      );
    },
    [adapter, eventingContext],
  );
  const trackConversion = useCallback(
    (conversion: ConversionInput): void => {
      adapter.handleConversion?.(
        {
          ...conversion,
          eventId: globalThis.crypto.randomUUID(),
          timestamp: Date.now(),
        },
        eventingContext,
      );
    },
    [adapter, eventingContext],
  );
  const eventingApi = useMemo<EventingApi>(
    () => ({ trackPageView, trackConversion }),
    [trackConversion, trackPageView],
  );

  useEffect(() => {
    if (!observationRoot || !viewObservationRoot) {
      return;
    }

    const teardownInteractions = subscribeDelegatedInteractions({
      root: observationRoot,
      interactionRegistry,
      registryPolicy,
      onUnknownComponentId,
      onInteraction: (interaction) => {
        adapter.handleInteraction(interaction, eventingContext);
      },
    });

    const teardownViews = startViewTracker({
      root: viewObservationRoot,
      viewRegistry,
      registryPolicy,
      onUnknownComponentId,
      onView: (view) => {
        adapter.handleView(view, eventingContext);
      },
    });

    return () => {
      teardownInteractions();
      teardownViews();
    };
  }, [
    adapter,
    eventingContext,
    interactionRegistry,
    observationRoot,
    onUnknownComponentId,
    registryPolicy,
    viewObservationRoot,
    viewRegistry,
  ]);

  return (
    <EventingApiContext.Provider value={eventingApi}>
      {children}
    </EventingApiContext.Provider>
  );
}

export function useEventing(): EventingApi | null {
  return useContext(EventingApiContext);
}
